import _ from 'lodash';
import { IObservable, Subject } from '~/rage/utils';
import { CharacterLicense } from '~/shared';
import { BackendService } from '../BackendService';
import { GameConfigService } from '../GameConfigService';
import { ILicense, ILicensesService } from './types';

interface ILicenseData {
	licenseId: CharacterLicense;
	createdAt: number;
	expiresAt: number | null;
}

export class LicensesService implements ILicensesService {
	private licenses = new Subject<ILicense[]>([]);

	private ownedLicensesMap = new Map<CharacterLicense, ILicenseData>();

	constructor(
		private readonly backend: BackendService,
		private readonly gameConfig: GameConfigService,
	) {
		this.backend.addEventHandler(
			'licenses:add',
			(data: ILicenseData | ILicenseData[]) => {
				const licenses = _.castArray(data);
				for (const license of licenses) {
					this.ownedLicensesMap.set(license.licenseId, license);
					this.updateLicenses();
				}
			},
		);

		this.backend.addEventHandler(
			'licenses:remove',
			(licenseId: CharacterLicense) => {
				this.ownedLicensesMap.delete(licenseId);
				this.updateLicenses();
			},
		);

		this.gameConfig.getConfigs().subscribe(() => {
			this.updateLicenses();
		});
	}

	public getLicenses(): IObservable<ILicense[]> {
		return this.licenses;
	}

	public getLicenseName(licenseId: CharacterLicense): string {
		const license = this.gameConfig.configs?.gameplay?.licenses.find(
			(license) => license.id === licenseId,
		);
		if (license === undefined) {
			return 'Неизвестная лицензия';
		}

		return license.name;
	}

	public getLicenseLetter(licenseId: CharacterLicense): string {
		const license = this.gameConfig.configs?.gameplay?.licenses.find(
			(license) => license.id === licenseId,
		);
		if (license === undefined) {
			return 'Неизвестная лицензия';
		}

		return license.letter;
	}

	public hasLicense(licenseId: CharacterLicense): boolean {
		return this.ownedLicensesMap.has(licenseId);
	}

	private updateLicenses() {
		if (!this.gameConfig.configs) {
			return;
		}

		const rawLicenses = this.gameConfig.configs.gameplay?.licenses ?? [];
		const licenses = rawLicenses.map((license) => {
			return {
				id: license.id,
				name: license.name,
				letter: license.letter,
				isOwned: this.ownedLicensesMap.has(license.id),
				durationDays: license.durationDays ?? undefined,
				expiresAt:
					this.ownedLicensesMap.get(license.id)?.expiresAt ?? undefined,
			};
		});

		this.licenses.next(licenses);
	}
}
